/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * AI ĐANG GIỮ QUYỀN ĐIỀU KHIỂN MỘT NGÕ RA — `OutputRuntime.nguonGiuQuyen` (NV108).
 *
 * Luật điều khiển lee chốt 08/09 (`_BUS/50_PHIEN.md`, **bản duy nhất đúng**):
 *
 *     NGUON_GIU   : KHÔNG AI | CONSOLE | PHẦN CỨNG | APP | MÀN HÌNH
 *     CHIẾM QUYỀN : nguồn nào ra lệnh → nguồn đó GIỮ. Đến sau ghi đè đến trước.
 *     GIẢI TOẢ    : phải là lệnh giải toả TỪ CHÍNH NGUỒN ĐANG GIỮ.
 *     ĐỒNG BỘ     : mỗi lần đổi → đẩy <trạng thái + TÊN NGUỒN ĐANG GIỮ> về TẤT CẢ các nguồn.
 *
 * ⇒ Nhãn cũ *"Đang giữ tay"* trả lời sai câu hỏi: nó nói **CÓ người giữ**, không nói **AI**.
 *   Mà *"phải là lệnh giải toả từ chính nguồn đang giữ"* biến **AI** thành thứ người dùng
 *   BẮT BUỘC phải biết — không biết ai giữ thì không biết phải đi đâu để trả quyền.
 *
 * ⛔ BA CÂU TRẢ LỜI, KHÔNG PHẢI HAI — và trộn chúng là đúng cái bệnh cả tuần nay:
 *
 *     'chua-biet'   app CHƯA nhận khung `CabinetState` nào cho tủ này
 *     'khong-ai'    tủ khai `GIU_KHONG_AI` (hoặc tủ đời cũ không có trường) ⇒ lịch chạy
 *     <một nguồn>   tủ khai đích danh
 *     'la'          mã ngoài bảng ⇒ KHÔNG được vẽ thành một cái tên
 *
 * ⚠ `'chua-biet'` KHÁC `'khong-ai'`. *"Tôi chưa nghe tủ nói gì"* và *"tủ nói không ai giữ"* là
 *   hai câu ngược hướng nhau về mức tin cậy, và gộp lại là biến một chỗ trống thành một lời
 *   khẳng định — đúng họ `0.000 A` (NV5) và `0 lớp TẮT` (YC-50).
 *
 * ⛔ TỦ ĐỜI CŨ đọc ra `'khong-ai'` là ĐÚNG, không phải một chỗ hổng: chúng không có khái niệm
 *   nguồn giữ, nên chúng **không thể** khẳng định một nguồn nào cả. Hỏng về phía "lịch chạy
 *   bình thường", tức là về phía an toàn.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

export type NguonGiu = 'khong-ai' | 'console' | 'phan-cung' | 'app' | 'man-hinh';
export type DocNguonGiu = NguonGiu | 'chua-biet' | 'la';

/** Đúng số hiệu enum `NguonGiuQuyen` trong `kapp_v2.proto`. */
export const MA_NGUON_GIU: Record<number, NguonGiu> = {
  0: 'khong-ai',
  1: 'console',
  2: 'phan-cung',
  3: 'app',
  4: 'man-hinh',
};

export function docNguonGiu(raw: unknown): DocNguonGiu {
  if (raw === undefined || raw === null) return 'chua-biet';
  const v = Number(raw);
  if (!Number.isInteger(v)) return 'la';
  return MA_NGUON_GIU[v] ?? 'la';
}

/** Tên hiển thị. `chua-biet` nói ra là chưa biết, KHÔNG mượn tên của `khong-ai`. */
export const TEN_NGUON_GIU: Record<DocNguonGiu, string> = {
  'chua-biet': 'chưa biết (tủ chưa báo về)',
  'khong-ai': 'không ai — lịch đang chạy',
  console: 'CONSOLE (lệnh AT trên cổng debug)',
  'phan-cung': 'CÔNG TẮC VẬT LÝ trên tủ',
  app: 'ỨNG DỤNG (chính trang này)',
  'man-hinh': 'MÀN HÌNH CẢM ỨNG trên tủ',
  la: 'mã lạ — không đọc được',
};

/** `true` khi có MỘT nguồn đang giữ. `chua-biet` và `la` KHÔNG tính là có người giữ. */
export const dangCoNguoiGiu = (n: DocNguonGiu): boolean =>
  n === 'console' || n === 'phan-cung' || n === 'app' || n === 'man-hinh';

/**
 * Tên NGẮN, dùng cho NHÃN. Bảng riêng với `TEN_NGUON_GIU` vì nhãn và câu văn trả lời hai câu
 * hỏi khác nhau (*"ai giữ"* trong ba chữ · *"đi đâu để trả quyền"* trong một câu).
 * ⚠ Hai bảng ⇒ hai nguồn cho cùng một sự thật. Có phép canh buộc chúng phủ **đúng cùng tập
 *   khoá** với `MA_NGUON_GIU`, đúng luật đã đặt cho `pha.ts`/`baove.ts`.
 */
export const TEN_NGAN_NGUON_GIU: Record<NguonGiu, string> = {
  'khong-ai': 'không ai',
  console: 'CONSOLE',
  'phan-cung': 'CÔNG TẮC',
  app: 'ỨNG DỤNG',
  'man-hinh': 'MÀN HÌNH TỦ',
};

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * NHÃN CHO MỘT NGÕ — và nó phải sống được trên **CẢ HAI ĐỜI PHẦN SỤN CÙNG LÚC**.
 *
 * Luật phiên: *"Nửa lên trước phải sống được một mình."* Hôm nay tủ **chưa có `NV108`**, nên
 * `nguonGiuQuyen` chưa đi trên dây và nhân chứng duy nhất là `manualHold` — một cờ nói **CÓ**
 * người giữ mà không nói **AI**. Sau khi nạp `P-2`, `nguonGiuQuyen` mới là lời khai đích danh.
 *
 *     có tên       ⇒ 'dich-danh'  — nói thẳng ai giữ
 *     manualHold   ⇒ 'chua-ro'    — CÓ người giữ, KHÔNG bịa ra tên   ← đời tủ hôm nay
 *     không gì cả  ⇒ 'trong'      — không dấu nào
 *
 * ⛔ Vì sao `manualHold` KHÔNG bị bỏ đi khi tủ khai `'khong-ai'`: tủ đời cũ khai `'khong-ai'`
 *   **vì nó không có khái niệm nguồn giữ**, không phải vì nó đã kiểm tra và thấy trống. Bỏ
 *   `manualHold` lúc ấy là **đánh rơi nhân chứng thật duy nhất đang có** — hỏng về phía
 *   *"trông như không ai giữ"*, tức phía NGUY.
 *
 * ⛔ Và `'chua-biet'`/`'la'` KHÔNG BAO GIỜ ra `'dich-danh'`: một mã ngoài bảng vẽ thành một
 *   cái tên là đúng cái bệnh `0.000 A`, chỉ khác chỗ đứng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export type NhanGiu =
  | { kieu: 'trong' }
  | { kieu: 'chua-ro'; nhan: string }
  | { kieu: 'dich-danh'; nguon: NguonGiu; nhan: string };

export function nhanGiuNgo(raw: unknown, giuTay: boolean): NhanGiu {
  const n = docNguonGiu(raw);
  if (dangCoNguoiGiu(n)) {
    return { kieu: 'dich-danh', nguon: n as NguonGiu, nhan: `Đang giữ bởi: ${TEN_NGAN_NGUON_GIU[n as NguonGiu]}` };
  }
  if (giuTay) return { kieu: 'chua-ro', nhan: 'Đang giữ — chưa rõ nguồn' };
  return { kieu: 'trong' };
}

/**
 * Câu chỉ đường khi người dùng muốn TRẢ QUYỀN.
 *
 * ⛔ NÓI NƠI PHẢI ĐI, KHÔNG ĐOÁN KẾT QUẢ. Câu này **không** viết *"bấm sẽ bị từ chối"* — đó là
 *   một lời tiên đoán về hành vi của tủ, và app chỉ được nói về SỐ ĐO nó nhận được (YC-47).
 *   Nó nói AI đang giữ và TRẢ QUYỀN PHẢI LÀM Ở ĐÂU — cả hai đều suy từ luật, không từ đoán.
 *
 * ⛔ Và nó KHÔNG chặn nút. Luật lee: *"Cảnh báo hỗ trợ KHÔNG ĐƯỢC chặn quyền điều khiển.
 *   Cảnh báo để NÓI, không để CHẶN."* Người dùng vẫn bấm được; tủ mới là nơi quyết.
 */
export function cauTraQuyen(n: DocNguonGiu): string | null {
  switch (n) {
    case 'app':
      return null;   // app giữ ⇒ nút trả quyền ở đây làm đúng việc của nó, không cần nói gì
    case 'man-hinh':
      // TK-14 C2.6 (03/10/2026): câu cũ "Trả quyền phải thực hiện tại màn hình đó" chỉ đường
      // tới một chỗ KHÔNG có nút trả quyền. Từ firmware TK-14 C2.2, màn hình gạt ngõ về TẮT
      // = nhả quyền của màn hình. ⚠ Tủ còn chạy firmware trước TK-14 thì gạt TẮT vẫn GIỮ quyền.
      // TK-24: rút gọn thành trạng thái + việc cần làm (tệp 29 điều 6), nghĩa giữ nguyên.
      return 'Màn hình tủ đang giữ — gạt ngõ này về TẮT trên màn hình tủ.';
    case 'phan-cung':
      return 'Công tắc vật lý trên tủ đang giữ — gạt công tắc tại tủ.';
    case 'console':
      return 'Console (cổng debug) đang giữ — trả quyền từ chính console đó.';
    case 'khong-ai':
    case 'chua-biet':
    case 'la':
      return null;
  }
}
